import { Role } from '../role';
import { deepFreeze } from './freeze';
import { PERMISSION_KEYS, type PermissionKey } from './permissions';

// Predefined (system) roles — `auth:PermissionCatalog@v1`, brief §4.17.
//
// System roles are immutable and versioned with the code: a release may add a permission
// to one, an admin may only duplicate it into a custom role. Each list below is exactly the
// ✓ cells of that role's column in the §4 matrix, written out key by key on purpose — a
// permission added to the catalog is granted to NO system role (except `admin`) until a
// release says so. Fail closed, never by omission.

/**
 * The four legacy `Role` values. During M1 (brief §8.4) today's `@RequireRole` / `hasRole`
 * / domain guards still evaluate ONE legacy role; every role (system or custom) declares a
 * `baseTier` used only to derive it. Retired with role-based guards in R9/R10.
 */
export type LegacyTier = 'admin' | 'ops_supervisor' | 'airport_operator' | 'finance_audit';

/** Derivation precedence, highest first. See {@link deriveLegacyRole}. */
export const LEGACY_TIERS: readonly LegacyTier[] = Object.freeze([
  'admin',
  'ops_supervisor',
  'airport_operator',
  'finance_audit',
] as const);

const TIER_ROLE: Readonly<Record<LegacyTier, Role>> = Object.freeze({
  admin: Role.ADMIN,
  ops_supervisor: Role.OPS_SUPERVISOR,
  airport_operator: Role.AIRPORT_OPERATOR,
  finance_audit: Role.FINANCE_AUDIT,
});

export function isLegacyTier(x: unknown): x is LegacyTier {
  return typeof x === 'string' && (LEGACY_TIERS as readonly string[]).includes(x);
}

/**
 * The legacy `Role` that today's role-based guards evaluate for a user holding roles with
 * these `baseTier`s: the highest by precedence **admin > ops_supervisor > airport_operator
 * > finance_audit**. No tiers (or only unknown strings) → `null`: the caller must treat
 * that as "no access", never as a default role.
 *
 * Non-linear corner: the legacy hierarchy is not a chain — `ops_supervisor` does NOT
 * include `finance_audit` (`RoleHierarchy`). A user holding a SUP-tier role AND a FIN-tier
 * role derives `ops_supervisor`, so any guard that accepts `finance_audit` but not
 * `ops_supervisor` is not satisfied for them through the legacy role (none exists today:
 * FIN reads are `@RequireAnyRole(OPS_SUPERVISOR, FINANCE_AUDIT)`, billing/reports are
 * UI-gated — but a future FIN-only gate would hit this). No single legacy role expresses SUP ∪ FIN;
 * the only one that would is `admin`, and promoting to it would grant users.* — an
 * escalation. M1 accepts the narrower answer; permission-based guards (R9/R10) remove it.
 */
export function deriveLegacyRole(tiers: readonly LegacyTier[]): Role | null {
  if (!Array.isArray(tiers)) return null;
  for (const tier of LEGACY_TIERS) {
    if (tiers.includes(tier)) return TIER_ROLE[tier];
  }
  return null;
}

export type SystemRoleKey =
  | 'admin'
  | 'ops_supervisor'
  | 'airport_operator'
  | 'counter_agent'
  | 'finance_audit'
  | 'config_manager'
  | 'viewer';

export interface ISystemRoleDef {
  key: SystemRoleKey;
  /** Spanish display name (§4.17 "Nombre"). */
  name: string;
  /** Spanish description (§4.17 "Para quién"). */
  description: string;
  /** Exactly this role's ✓ cells in the §4 matrix (catalog order). `admin` holds every key. */
  permissions: readonly PermissionKey[];
  /** Legacy tier for M1 role derivation only (brief §8.4). */
  baseTier: LegacyTier;
  system: true;
}

const OPS_SUPERVISOR: readonly PermissionKey[] = [
  // §4.1
  'dashboard.view',
  'passengers.search',
  // §4.2
  'disruptions.view',
  'disruptions.declare',
  'disruptions.run',
  'disruptions.contain',
  'disruptions.contain.force',
  'disruptions.pnr.exclude',
  'disruptions.repair',
  // §4.3 (not cases.resolve — ADM only)
  'cases.view',
  'cases.create',
  'cases.pnr.add',
  'cases.care_only',
  'cases.outcomes.declare',
  // §4.4
  'flights.view',
  // §4.5
  'rebooking.generate',
  'rebooking.generate.force',
  'rebooking.select',
  'rebooking.override',
  'rebooking.override.waive_rule',
  'rebooking.ticket.issue',
  'rebooking.ticket.issue_bulk',
  'rebooking.external',
  'rebooking.lcc.search',
  'rebooking.lcc.purchase',
  // §4.6
  'acceptance.force',
  'acceptance.force_all',
  'notifications.send',
  // §4.7
  'hotels.allocate',
  'hotels.book',
  'hotels.policy.override',
  'hotels.release',
  'hotels.stay',
  'hotels.rooming.export',
  // §4.8
  'transport.plan',
  'transport.operate',
  'transport.vouchers',
  'transport.manifest.export',
  // §4.9
  'care.view',
  'care.issue',
  'care.issue.custom',
  'care.topup',
  'care.topup.over_cap',
  'care.redeem',
  'care.void',
  'care.token.reveal',
  'benefits.override.propose',
  'benefits.override.decide',
  'benefits.override.apply',
  // §4.10
  'pii.name.reveal',
  'pii.contact.reveal',
  'pii.document.reveal',
  'pii.bulk',
  'pii.contact.edit',
  'pii.special_needs',
  // §4.11 (not billing.view)
  'costs.view',
  'reconciliation.view',
  'reports.view',
  'reports.export',
  'activity.view',
  'audit.view',
  // §4.12
  'manifests.import',
  // §4.13
  'reservations.view',
  'reservations.purchase',
  'reservations.transport',
  // §4.14
  'counters.qr',
  'manual_review.resolve',
  'provider_sim.answer',
  // §4.15
  'policies.view',
  'policies.edit',
  'policies.activate',
  'contracts.view',
  'contracts.edit',
  'formats.view',
  'formats.edit',
  'formats.approve',
  'providers.view',
  'providers.test',
  'providers.toggle',
  // §4.16: none
];

const AIRPORT_OPERATOR: readonly PermissionKey[] = [
  // §4.1
  'dashboard.view',
  'passengers.search',
  // §4.2
  'disruptions.view',
  'disruptions.declare',
  'disruptions.run',
  'disruptions.pnr.exclude',
  // §4.3
  'cases.view',
  'cases.create',
  'cases.pnr.add',
  'cases.outcomes.declare',
  // §4.4
  'flights.view',
  // §4.5
  'rebooking.generate',
  'rebooking.select',
  'rebooking.override',
  'rebooking.ticket.issue',
  'rebooking.external',
  'rebooking.lcc.search',
  // §4.6
  'acceptance.force',
  'notifications.send',
  // §4.7
  'hotels.allocate',
  'hotels.book',
  'hotels.release',
  'hotels.stay',
  'hotels.rooming.export',
  // §4.8
  'transport.plan',
  'transport.operate',
  'transport.vouchers',
  'transport.manifest.export',
  // §4.9 (care.issue = entitlement-bounded only, brief §4.9 ¹ / §8.5)
  'care.view',
  'care.issue',
  'care.topup',
  'care.redeem',
  'benefits.override.propose',
  // §4.10
  'pii.name.reveal',
  'pii.contact.reveal',
  'pii.bulk',
  'pii.contact.edit',
  'pii.special_needs',
  // §4.11
  'costs.view',
  // §4.12
  'manifests.import',
  // §4.13
  'reservations.view',
  // §4.14
  'counters.qr',
  // §4.15
  'formats.view',
  'formats.edit',
];

const COUNTER_AGENT: readonly PermissionKey[] = [
  // §4.1
  'dashboard.view',
  'passengers.search',
  // §4.2
  'disruptions.view',
  // §4.3
  'cases.view',
  'cases.outcomes.declare',
  // §4.4
  'flights.view',
  // §4.6
  'acceptance.force',
  'notifications.send',
  // §4.8
  'transport.operate',
  // §4.9
  'care.view',
  'care.redeem',
  // §4.10
  'pii.name.reveal',
  'pii.contact.reveal',
  'pii.contact.edit',
  'pii.special_needs',
  // §4.14
  'counters.qr',
];

const FINANCE_AUDIT: readonly PermissionKey[] = [
  // §4.1
  'dashboard.view',
  'passengers.search',
  // §4.2
  'disruptions.view',
  // §4.3
  'cases.view',
  // §4.4
  'flights.view',
  // §4.9
  'care.view',
  // §4.11
  'costs.view',
  'reconciliation.view',
  'reports.view',
  'reports.export',
  'activity.view',
  'audit.view',
  'billing.view',
  // §4.13
  'reservations.view',
  // §4.15
  'policies.view',
  'contracts.view',
  // §4.16
  'users.export',
  'roles.view',
];

const CONFIG_MANAGER: readonly PermissionKey[] = [
  // §4.15
  'policies.view',
  'policies.edit',
  'policies.activate',
  'contracts.view',
  'contracts.edit',
  'formats.view',
  'formats.edit',
  'formats.approve',
  'providers.view',
  'providers.test',
  'providers.toggle',
];

// §4.17 "sin PII ni costos": no 🔒 / 💰 key — so not `passengers.search` (🔒), brief §11 B9.
const VIEWER: readonly PermissionKey[] = [
  'dashboard.view',
  'disruptions.view',
  'cases.view',
  'flights.view',
  'care.view',
];

/** Re-sort into catalog order so every role lists its keys the way the matrix reads. */
function inCatalogOrder(keys: readonly PermissionKey[]): PermissionKey[] {
  const held = new Set(keys);
  return PERMISSION_KEYS.filter((k) => held.has(k));
}

/** The seven system roles, in §4.17 order. Deep-frozen. */
export const SYSTEM_ROLES: readonly ISystemRoleDef[] = deepFreeze<ISystemRoleDef[]>([
  {
    key: 'admin',
    name: 'Administrador',
    description: 'Responsable de la cuenta de la aerolínea: todo, incl. usuarios y roles',
    permissions: [...PERMISSION_KEYS],
    baseTier: 'admin',
    system: true,
  },
  {
    key: 'ops_supervisor',
    name: 'Supervisor de operaciones',
    description:
      'Jefe de turno / OCC: toda la operación incl. acciones forzadas, gasto, excepciones y configuración operativa',
    permissions: inCatalogOrder(OPS_SUPERVISOR),
    baseTier: 'ops_supervisor',
    system: true,
  },
  {
    key: 'airport_operator',
    name: 'Operador de aeropuerto',
    description: 'Agente que resuelve el evento día a día dentro de la política',
    permissions: inCatalogOrder(AIRPORT_OPERATOR),
    baseTier: 'airport_operator',
    system: true,
  },
  {
    key: 'counter_agent',
    name: 'Agente de mostrador',
    description:
      'Mostrador / puerta: buscar al pasajero, aceptar en persona, canjear asistencia, check-in, reenviar info',
    permissions: inCatalogOrder(COUNTER_AGENT),
    baseTier: 'airport_operator',
    system: true,
  },
  {
    key: 'finance_audit',
    name: 'Finanzas y auditoría',
    description: 'Control de costos, conciliación, reportes, facturación, auditoría — solo lectura',
    permissions: inCatalogOrder(FINANCE_AUDIT),
    baseTier: 'finance_audit',
    system: true,
  },
  {
    key: 'config_manager',
    name: 'Configuración',
    description: 'Políticas, contratos, formatos e integraciones; sin operación',
    permissions: inCatalogOrder(CONFIG_MANAGER),
    // Policy / contract writes are ≥ ops_supervisor in today's guards.
    baseTier: 'ops_supervisor',
    system: true,
  },
  {
    key: 'viewer',
    name: 'Solo lectura',
    description: 'Gerencia / autoridad aeroportuaria: ver el estado, sin PII ni costos',
    permissions: inCatalogOrder(VIEWER),
    // The least-privileged legacy tier.
    baseTier: 'finance_audit',
    system: true,
  },
]);

export const SYSTEM_ROLE_KEYS: readonly SystemRoleKey[] = Object.freeze(SYSTEM_ROLES.map((r) => r.key));

export function isSystemRoleKey(x: unknown): x is SystemRoleKey {
  return typeof x === 'string' && (SYSTEM_ROLE_KEYS as readonly string[]).includes(x);
}

/** The system role with this key, or `undefined`. */
export function systemRole(key: string): ISystemRoleDef | undefined {
  return SYSTEM_ROLES.find((r) => r.key === key);
}
